import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ReactComponent as ChatBubbleIcon } from "assets/mail-chat-bubble-square-warning.svg";
import Scroller from "components/common/Scroller/InfiniteScroller";
import ActionBar from "components/features/dashboard/ActionBar";
import { uniqueId } from "lodash";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { brandDeadlinesType, initiativePlansType, missingIncodeText, PAGE_SIZE } from "utils/CommonConstants";
import { routes } from "utils/routes";
import {
  fetchCardsOverviewReportAsync,
  fetchDashboardPlansAsync,
  fetchPlansOverviewReportAsync
} from "../Dashboard.slice";

export const TAB = {
  plans: "plans",
  initiatives: "initiatives",
  brandDeadline: "brandDeadline",
  actions: "actions"
};

export const DISPLAY_TYPE = {
  noPlans: "no_plans",
  myPlansOverview: "my_plans_overview",
  highlights: "highlights",
  userDashboardMyPlans: "user_dashboard_MyPlans",
  userDashboardHighlights: "user_dashboard_Highlights"
};

const NoDataGraphic = (type: string, tab: string) => {
  const selectedTab = tab === TAB.brandDeadline ? "Brand Deadlines" : "Initiatives";
  const message =
    type === DISPLAY_TYPE.highlights
      ? `There are no active ${selectedTab}.`
      : `
  You still need to add plans. No problem! Review Suggested Plans, search the Library, or retake the Wizard to find your biggest opportunities.`;

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.Icon.General.Secondary}`,
        borderRadius: "8px",
        padding: type === DISPLAY_TYPE.highlights ? "8px 16px" : "8px 40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "24px",
        textAlign: "center",
        minHeight: "280px",
        justifyContent: "center"
      }}
    >
      <ChatBubbleIcon />
      <Typography variant="body1" color={tokens.Text.General.Tertiary}>
        {message}
      </Typography>
    </Box>
  );
};

const MyPlansOverview = (props: any) => {
  const { tab } = props;
  const dispatch = useAppDispatch();
  let innCode = useAppSelector((state: any) => state.app.innCode);
  const plansList: any = useAppSelector((state) => state?.Dashboard.plansReport);
  const cardsList: any = useAppSelector((state) => state?.Dashboard.cardsReport);
  const list = tab === TAB.actions ? cardsList?.list : plansList?.list;
  const totalRecords = tab === TAB.actions ? cardsList?.totalRecords : plansList?.totalRecords;
  const [plansPageNo, setPlansPageNo] = useState(0);
  const [actionsPageNo, setActionsPageNo] = useState(0);
  const [searchParams] = useSearchParams();
  const externalHltId = searchParams.get("member");
  const selectedInncode = searchParams.get("inncode");
  innCode = selectedInncode ?? innCode;

  useEffect(() => {
    setPlansPageNo(0);
    setActionsPageNo(0);
  }, [innCode]);

  const fetchMore = () => {
    if (tab === TAB.plans) setPlansPageNo(plansPageNo + 1);
    else setActionsPageNo(actionsPageNo + 1);
  };

  useEffect(() => {
    if (innCode !== missingIncodeText && tab === TAB.plans) {
      dispatch(fetchPlansOverviewReportAsync({ innCode, page: plansPageNo, externalHltId }));
    }
  }, [innCode, plansPageNo]);

  useEffect(() => {
    if (innCode !== missingIncodeText && tab === TAB.actions) {
      dispatch(fetchCardsOverviewReportAsync({ innCode, page: actionsPageNo, externalHltId }));
    }
  }, [innCode, actionsPageNo]);

  return (
    <>
      {list?.length > 0 ? (
        <Box
          id="actionItemsDiv2"
          sx={{
            height: "300px",
            overflowY: "scroll",
            marginY: "-8px",
            marginRight: "-10px",
            paddingRight: "10px"
          }}
        >
          <Scroller
            dataLength={list?.length}
            loadMore={fetchMore}
            totalList={totalRecords}
            label={tab === TAB.plans ? "Plans" : "Actions"}
            scrollableTarget={"actionItemsDiv2"}
          >
            {list?.map((element: any) => {
              return (
                <ActionBar
                  type={DISPLAY_TYPE.myPlansOverview}
                  tab={tab}
                  showDetails
                  dateDetails={tab === TAB.brandDeadline || tab === TAB.initiatives}
                  planName={element?.planName}
                  key={uniqueId("OVERVIEW_")}
                  element={element}
                  url={
                    tab === TAB.actions
                      ? `/myplandetails/${element?.planId}/${element?.sectionId}/${element?.cardId}`
                      : `/myplandetails/${element?.id}`
                  }
                />
              );
            })}
          </Scroller>
        </Box>
      ) : (
        NoDataGraphic(DISPLAY_TYPE.myPlansOverview, tab)
      )}
    </>
  );
};

const Highlights = (props: any) => {
  const { tab } = props;
  const dispatch = useAppDispatch();
  const brandDeadlines: any = useAppSelector((state) => state?.Dashboard?.brandDeadlines);
  const initiatives: any = useAppSelector((state) => state?.Dashboard?.initiativePlans);
  let innCode = useAppSelector((state: any) => state.app.innCode);
  const [initiativePageNo, setInitiativePageNo] = useState(1);
  const [brandDeadlinePageNo, setBrandDeadlinePageNo] = useState(1);
  const [searchParams] = useSearchParams();
  const externalHltId = searchParams.get("member");
  const selectedInncode = searchParams.get("inncode");
  innCode = selectedInncode ?? innCode;

  useEffect(() => {
    setBrandDeadlinePageNo(1);
    setInitiativePageNo(1);
  }, [innCode]);

  useEffect(() => {
    if (innCode !== missingIncodeText && tab === TAB.brandDeadline)
      dispatch(
        fetchDashboardPlansAsync({
          filter: {
            innCode
          },
          pagination: {
            page: brandDeadlinePageNo,
            size: PAGE_SIZE
          },
          planType: [brandDeadlinesType],
          sort: {
            property: "brandDeadline",
            direction: "asc"
          },
          externalHltId
        })
      );
  }, [innCode, brandDeadlinePageNo]);

  useEffect(() => {
    if (innCode !== missingIncodeText && tab === TAB.initiatives)
      dispatch(
        fetchDashboardPlansAsync({
          filter: {
            innCode
          },
          pagination: {
            page: initiativePageNo,
            size: PAGE_SIZE
          },
          planType: [initiativePlansType],
          externalHltId
        })
      );
  }, [innCode, initiativePageNo]);

  const fetchMore = () => {
    if (tab === TAB.brandDeadline) setBrandDeadlinePageNo(brandDeadlinePageNo + 1);
    else setInitiativePageNo(initiativePageNo + 1);
  };

  const getDetails = (tab: string) => {
    switch (tab) {
      case TAB.brandDeadline:
        return { list: brandDeadlines?.list, totalPlans: brandDeadlines?.totalRecords };
      default:
        return { list: initiatives?.list, totalPlans: initiatives?.totalRecords };
    }
  };

  const { list, totalPlans } = getDetails(tab);

  return (
    <>
      {list?.length > 0 ? (
        <Box
          id="actionItemsDiv1"
          sx={{
            height: "320px",
            overflowY: "scroll",
            marginY: "-8px",
            marginRight: "-10px",
            paddingRight: "10px"
          }}
        >
          <Scroller
            dataLength={list?.length}
            loadMore={fetchMore}
            totalList={totalPlans}
            label="Plans"
            scrollableTarget={"actionItemsDiv1"}
          >
            {list.map((element: any) => {
              return (
                <ActionBar
                  type={DISPLAY_TYPE.highlights}
                  tab={tab}
                  showDetails={tab === TAB.plans}
                  dateDetails={tab === TAB.brandDeadline || tab === TAB.initiatives}
                  key={uniqueId("HIGHLIGHT_")}
                  element={element}
                  url={`/plandetails/${element?.id}`}
                />
              );
            })}
          </Scroller>
        </Box>
      ) : (
        NoDataGraphic(DISPLAY_TYPE.highlights, tab)
      )}
    </>
  );
};

export const Config = {
  [DISPLAY_TYPE.highlights]: {
    color: colors.Neutral.Neutral700,
    title: "Upcoming",
    targetURL: (navigate: any) => {},
    tabs: [
      {
        label: "Brand Deadlines",
        content: Hoc(Highlights, TAB.brandDeadline)
      },
      {
        label: "Initiatives",
        content: Hoc(Highlights, TAB.initiatives)
      }
    ]
  },
  [DISPLAY_TYPE.myPlansOverview]: {
    color: colors.Neutral.Neutral800,
    title: "My Plans & Actions",
    buttonLabel: "Go to My Plans",
    targetURL: (navigate: any) => {
      navigate(routes.MyPlans);
    },
    tabs: [
      { label: "Plans", content: Hoc(MyPlansOverview, TAB.plans) },
      { label: "Actions", content: Hoc(MyPlansOverview, TAB.actions) }
    ]
  },
  [DISPLAY_TYPE.userDashboardMyPlans]: {
    color: colors.Neutral.Neutral800,
    title: "My Plans Overview",
    targetURL: (navigate: any) => {},
    tabs: [
      { label: "Plans", content: Hoc(MyPlansOverview, TAB.plans) },
      { label: "Actions", content: Hoc(MyPlansOverview, TAB.actions) }
    ]
  },
  [DISPLAY_TYPE.userDashboardHighlights]: {
    color: colors.Neutral.Neutral700,
    title: "Highlights",
    targetURL: (navigate: any) => {},
    tabs: [
      {
        label: "Brand Deadlines",
        content: Hoc(Highlights, TAB.brandDeadline)
      },
      {
        label: "Initiatives",
        content: Hoc(Highlights, TAB.initiatives)
      }
    ]
  }
};

function Hoc(WrappedComponent: any, tab: string) {
  return function (props: any) {
    return <WrappedComponent {...props} tab={tab} />;
  };
}
